import { useTranslation } from "react-i18next";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";
import {
  Box,
  Heading,
  Text,
  Icon,
  Link,
  Stack,
  Flex,
  Image,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";

import contactImage from "../../img/AC MAP-2.png"; 

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionFlex = motion(Flex);

export default function Contact() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const mainColor = "#000";
  const accentColor = "#c6a664";

  const contactData = t("contact", { returnObjects: true });
  const navbar = t("navbar", { returnObjects: true });

  const contactDetails = [
    {
      icon: FaPhoneAlt,
      label: isArabic ? "الهاتف" : "Phone",
      value: contactData.phone || "+966 11 213 9740",
      href: `tel:${contactData.phone?.replace(/\s/g, "") || "+966112139740"}`,
    },
    {
      icon: FaEnvelope,
      label: isArabic ? "البريد الإلكتروني" : "Email",
      value: contactData.email || "info@alphaunited.com.sa",
      href: `mailto:${contactData.email || "info@alphaunited.com.sa"}`,
    },
    {
      icon: FaMapMarkerAlt,
      label: isArabic ? "العنوان" : "Location",
      value:
        contactData.location ,
      href: `https://maps.app.goo.gl/yTPHe51UY47qdv8h9`,
    },
  ];

  return (
    <Box
      id="contact"
      bg={mainColor}
      py={12}
      px={6}
      dir={isArabic ? "rtl" : "ltr"}
      overflow="hidden"
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        maxW="6xl"
        mx="auto"
        align="flex-start"
        justify="space-between"
        gap={10}
      >
        {/* Left Section: Contact Info */}
        <MotionBox
          flex="1"
          initial={{ opacity: 0, x: isArabic ? 50 : -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <MotionHeading
            fontSize="2xl"
            color="gray.100"
            mb={4}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            {contactData.title || (isArabic ? "تواصل معنا" : "Get in Touch")}
          </MotionHeading>

          <MotionText
            fontSize="md"
            mb={6}
            color="gray.100"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {isArabic
              ? "نحن هنا للإجابة على استفساراتك ودعم احتياجاتك"
              : "We're here to answer your questions and support your need"}
          </MotionText>

          <Stack spacing={4}>
            {contactDetails.map((item, index) => (
              <MotionFlex
                key={index}
                align="center"
                gap={4}
                initial={{ opacity: 0, x: isArabic ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                viewport={{ once: true }}
              >
                <Flex align="center" gap={4}>
                  <Icon
                    as={item.icon}
                    color="gray.100"
                    boxSize={5}
                    _hover={{ color: accentColor }}
                  />
                  <Box>
                    <Text
                      fontSize="sm"
                      color="gray.100"
                      fontWeight="medium"
                      _hover={{ color: accentColor }}
                    >
                      {item.label}
                    </Text>
                    <Link
                      href={item.href}
                      isExternal={!!item.href}
                      _hover={{ textDecoration: "none", color: accentColor }}
                    >
                    <Text
                        fontSize="md"
                        fontWeight="bold"
                        color="gray.100"
                        dir={isArabic ? "rtl" : "ltr"}
                        textAlign={isArabic ? "right" : "left"}
                        _hover={{ color: accentColor }}
                      >
                        {isArabic ? <span dir="ltr">{item.value}</span> : item.value}
                      </Text>
                    </Link>
                  </Box>
                </Flex>
              </MotionFlex>
            ))}
          </Stack>
        </MotionBox>

        {/* Middle Section: Quick Links */}
        <MotionBox
          flex="1"
          w="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          px={4}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Heading
            fontSize="lg"
            color="gray.100"
            mb={4}
            textAlign={isArabic ? "right" : "left"}
          >
            {isArabic ? "روابط سريعة" : "Quick Links"}
          </Heading>

          <Stack spacing={3} textAlign={isArabic ? "right" : "left"}>
            {[
              { label: navbar.home, to: "hero" },
              { label: navbar.about, to: "overview" },
              { label: navbar.visionMission, to: "vision-mission" },
              { label: navbar.businesses, to: "businesses" },
              { label: navbar.careers, to: "careers" },
              { label: navbar.contact, to: "contact" },
            ].map((item, index) => (
              <ScrollLink
                key={index}
                to={item.to}
                smooth
                duration={500}
                offset={-80}
              >
                <Text
                  color="gray.100"
                  fontSize="md"
                  fontWeight="medium"
                  _hover={{ color: accentColor }}
                  cursor="pointer"
                >
                  {item.label}
                </Text>
              </ScrollLink>
            ))}
          </Stack>
        </MotionBox>

{/* Right Section: Image */}
<MotionBox
  flex="1.2"
  display="flex"
  alignItems="center"
  justifyContent="center"
  initial={{ opacity: 0, x: isArabic ? -50 : 50 }}
  whileInView={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.8 }}
  viewport={{ once: true }}

>
  <Box
    position="relative"
    w="100%"
    maxW="550px"
    overflow="hidden"
    sx={{
      WebkitMaskImage:
        "radial-gradient(circle at center, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 80%)",
      maskImage:
        "radial-gradient(circle at center, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 80%)",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskSize: "100% 100%",
      maskSize: "100% 100%",
    }}
  >
    <Image
      src={contactImage}
      alt="Contact Section Image"
      borderRadius="md"
      objectFit="cover"
      w="100%"
      h="100%"
      maxH="380px"
      filter="brightness(0.9)"
      transition="all 0.4s ease"
      _hover={{ transform: "scale(1.03)" }}
    />
  </Box>
</MotionBox>

      </Flex>
    </Box>
  );
}
